import styles from './index.module.css'

import React from 'react'

import { SEARCH_APPEARS_ON_FILTER_FIELD } from '@disco/constants/search'

import type {
  FilterComponentProps,
  FilterOperator,
  FilterOperatorOption,
} from 'types/filter-page'

import { Flex, Text } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'
import {
  APPEARS_ON_OPERATOR_LABELS,
  getBetweenRangeErrors,
  getMissingValuePresets,
  NO_PLAYLISTS_PRESET,
} from '../../utils'
import { FilterErrorText } from '../components'
import { NumericInput } from '../components/numeric-input'
import { useNumericValidation } from './use-numeric-validation'

type NumberFormProps = FilterComponentProps & {
  yearOperators?: boolean
}

const NumberForm = ({
  label,
  field,
  value,
  onChange,
  operator = 'equals',
  yearOperators = false,
  onPress,
  onValidityChange,
}: NumberFormProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  // The "Appears on" (playlists count) field offers a "No playlists" shortcut
  // instead of the missing-value presets: a count is never missing.
  const isAppearsOn = field === SEARCH_APPEARS_ON_FILTER_FIELD
  const presets = isAppearsOn
    ? [NO_PLAYLISTS_PRESET]
    : getMissingValuePresets(label)
  const placeholder = isAppearsOn ? 'Playlists' : label
  const operatorLabel = (value: FilterOperator): string | undefined =>
    isAppearsOn ? APPEARS_ON_OPERATOR_LABELS[value] : undefined

  const { letterErrors, hasActiveLetterError, handleDigitChange, rawText } =
    useNumericValidation({ operator, value, onValidityChange })

  const { startMissing, endMissing, isIncomplete } =
    getBetweenRangeErrors(value)

  const singleInput = (
    <Flex direction="column" gap="1">
      <NumericInput
        value={rawText.single ?? (value || '')}
        onValueChange={raw =>
          handleDigitChange('single', raw, digits => onChange(digits, operator))
        }
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus
        hasError={letterErrors.single}
      />
      {letterErrors.single && (
        <FilterErrorText>Only numbers allowed</FilterErrorText>
      )}
    </Flex>
  )

  const allFilterOperators: FilterOperatorOption[] = [
    {
      operator: 'equals',
      label: operatorLabel('equals'),
      component: singleInput,
    },
    {
      operator: 'between',
      label: operatorLabel('between'),
      component: (
        <Flex direction="column" gap="1">
          <Flex gap="2" align="center" className={styles.betweenContainer}>
            <NumericInput
              value={rawText.start ?? (value?.[0] || '')}
              onValueChange={raw =>
                handleDigitChange('start', raw, digits =>
                  onChange([digits, value?.[1] ?? ''], operator),
                )
              }
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus
              hasError={letterErrors.start || startMissing}
              width="fluid"
            />
            <Text size="2">-</Text>
            <NumericInput
              value={rawText.end ?? (value?.[1] || '')}
              onValueChange={raw =>
                handleDigitChange('end', raw, digits =>
                  onChange([value?.[0] ?? '', digits], operator),
                )
              }
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              hasError={letterErrors.end || endMissing}
              width="fluid"
            />
          </Flex>
          {hasActiveLetterError ? (
            <FilterErrorText>Only numbers allowed</FilterErrorText>
          ) : (
            isIncomplete && <FilterErrorText>Enter both values</FilterErrorText>
          )}
        </Flex>
      ),
    },
    {
      operator: yearOperators ? 'lte' : 'lt',
      label: operatorLabel(yearOperators ? 'lte' : 'lt'),
      component: singleInput,
    },
    {
      operator: yearOperators ? 'gte' : 'gt',
      label: operatorLabel(yearOperators ? 'gte' : 'gt'),
      component: singleInput,
    },
  ]

  return (
    <FilterOperatorSelector
      operator={operator}
      value={value}
      operators={allFilterOperators}
      presets={presets}
      onChange={onChange}
      onKeyDown={handleKeyDown}
    />
  )
}

export default NumberForm
